import { catalogReferenceClient } from "../catalogApprovals/catalogReferenceOptions";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import useCatalogAccess from "../useCatalogAccess";
import { readStoredUser } from "../../../utils/sessionUser";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import InternalCodeSuggestions from "../InternalCodeSuggestions";
import { catalogRequestError, duplicateInternalCodeMessage, hasInternalCodeConflict } from "../internalCatalogCodes";
import { useCatalogTaxonomyText, useCatalogTaxonomyDialog } from "../catalogTaxonomyMessages";
import { useEffect, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";

const buildEmptyForm = () => ({
  code: "",
  name: "",
  description: "",
  family_id: "",
});

export default function AddSubfamilyPopup({ isOpen, onClose, onAdded, existingRecords = [] }) {
  const t = useCatalogTaxonomyText();
  const { languageId } = usePmsTheme();
  const businessUnitId = useRef(readStoredUser()?.active_business_unit_id).current;
  const access = useCatalogAccess(businessUnitId);
  const permission = "purchasing.items.create";
  const canCreate = access.can(permission);
  const [form, setForm] = useState(buildEmptyForm());
  const [families, setFamilies] = useState([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState("");
  const [pendingRequest, setPendingRequest] = useState(null);
  const dialogRef = useCatalogTaxonomyDialog(isOpen && canCreate && !pendingRequest, () => handleClose(), saving);

  useEffect(() => {
    if (!isOpen || !canCreate) return undefined;
    let cancelled = false;
    const stamp = access.capture();

    const loadFamilies = async () => {
      try {
        const response = await catalogReferenceClient(axios, languageId).get("/api/purchasing/families", access.requestConfig(permission));
        if (cancelled || !access.isCurrent(stamp, permission)) return;
        const sorted = [...(response.data || [])].sort((left, right) =>
          String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
            sensitivity: "base",
          })
        );
        setFamilies(sorted);
      } catch (requestError) {
        console.error("[SUBFAMILIES][ADD][FAMILIES][ERROR]", requestError);
        if (!cancelled && access.isCurrent(stamp, permission)) {
          setFamilies([]);
          setError("We could not load the family list for this popup.");
        }
      }
    };

    savingRef.current = false;
    setPendingRequest(null);
    setForm(buildEmptyForm());
    setSaving(false);
    setError("");
    loadFamilies();

    return () => {
      cancelled = true;
    };
  }, [isOpen, canCreate, languageId, access, permission]);

  if (!isOpen || !canCreate) return null;
  if (pendingRequest) return <CatalogCreationPendingNotice request={pendingRequest} onClose={onClose} />;

  const handleClose = () => {
    if (savingRef.current) return;
    setForm(buildEmptyForm());
    setError("");
    onClose();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (savingRef.current || !access.can(permission)) return;
    if (hasInternalCodeConflict(form.code, existingRecords)) {
      setError(duplicateInternalCodeMessage(form.code));
      return;
    }
    const stamp = access.capture();
    savingRef.current = true;
    setSaving(true);
    setError("");

    try {
      const response = await axios.post("/api/purchasing/subfamilies", { ...form, code: form.code.trim() }, access.requestConfig(permission));
      if (!access.isCurrent(stamp, permission)) return;
      if (isPendingCatalogCreation(response.data)) { setPendingRequest(response.data.request); setSaving(false); return; }
      if (typeof onAdded === "function") {
        await onAdded(response.data);
      }
      setForm(buildEmptyForm());
      onClose();
    } catch (requestError) {
      if (!access.isCurrent(stamp, permission)) return;
      console.error("[SUBFAMILIES][ADD][ERROR]", requestError);
      savingRef.current = false;
      setError(catalogRequestError(requestError, "Error saving subfamily.", form.code));
      setSaving(false);
    }
  };

  return (
    <div
      className="spx-modalBackdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >


      <div ref={dialogRef} className="spx-modal" role="dialog" aria-modal="true" aria-label={t("Add subfamily")}>
        <div className="spx-modalHeader">
          <div>
            <div className="spx-modalKicker">{t("Catalog Structure")}</div>
            <h3 className="spx-modalTitle">{t("Add Subfamily")}</h3>
            <p className="spx-modalSubtitle">{t("Select its family, then give this subfamily a code and name.")}</p>
          </div>

          <button type="button" className="spx-closeBtn" onClick={handleClose} disabled={saving} aria-label={t("Close add subfamily popup")}>
            x
          </button>
        </div>

        <form onSubmit={handleSubmit} className="spx-modalBody">
          <div className="spx-formGrid">
            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="subfamily_family">{t("Family")} </label>
              <select
                id="subfamily_family"
                name="family_id"
                value={form.family_id}
                onChange={handleChange}
                disabled={saving}
                className="spx-select"
                required
              >
                <option value="">{t("Select a family")}</option>
                {families.map((family) => (
                  <option key={family.id} value={family.id}>
                    {family.code ? `${family.code} - ` : ""}
                    {family.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="subfamily_name">{t("Subfamily Name")} </label>
              <input
                id="subfamily_name"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                disabled={saving}
                className="spx-input"
                placeholder={t("Example: Detergents")}
                required
              />
            </div>

            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="subfamily_code">{t("Subfamily Code")} </label>
              <input
                id="subfamily_code"
                type="text"
                name="code"
                value={form.code}
                onChange={handleChange}
                disabled={saving}
                className="spx-input"
                placeholder={t("Example: SF001")}
                required
              />
              <InternalCodeSuggestions name={form.name} value={form.code} resource="subfamilies" disabled={saving}
                onSuggest={(code) => { setForm((previous) => ({ ...previous, code })); setError(""); }} />
            </div>

            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="subfamily_description">{t("Description")} </label>
              <textarea
                id="subfamily_description"
                name="description"
                value={form.description}
                onChange={handleChange}
                disabled={saving}
                className="spx-textarea"
                placeholder={t("Optional operational description for the team maintaining this taxonomy slice.")}
              />
            </div>
          </div>

          {error ? <div className="spx-alert" role="alert">{t(error)}</div> : null}

          <div className="spx-modalFooter">
            <button type="button" className="spx-btn spx-btn--ghost" onClick={handleClose} disabled={saving}>{t("Cancel")} </button>
            <button type="submit" className="spx-btn spx-btn--primary" disabled={saving}>
              {t(saving ? "Saving..." : "Save Subfamily")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
